// ---------------------------------------------------------------------------
// AppContext — Cross-cutting UI state shared across pages.
//
// Contains: datasets and other shared state, plus the app-level telemetry
// wiring. Following the TMP `AppContextProvider` pattern, the telemetry logger
// is injected at boot as the `biLogger` prop; the provider derives a
// `scenarioFactory` from it, fires the one-time `app_init` boot scenario, and
// exposes both on the context so panels/hooks consume them off AppContext.
//
// Does NOT contain data-fetching logic — that belongs in hooks.
// ---------------------------------------------------------------------------

import {
  FluentProvider,
  teamsDarkTheme,
  teamsLightTheme,
} from '@fluentui/react-components';
import {
  useCallback,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';

import { STORAGE_KEYS } from '../constants';
import type { TelemetryLogger } from '../telemetry/logger';
import { createScenarioFactory } from '../telemetry/scenarioFactory.js';
import { ScenarioName } from '../telemetry/scenarios.js';

import { INITIAL_DATASETS } from './AppContext.data';
import type { AppState, ColorScheme } from './AppContext.interface';

const AppContext = createContext<AppState | null>(null);

function safeLocalStorageGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function getInitialColorScheme(): ColorScheme {
  const savedColorScheme = safeLocalStorageGetItem(STORAGE_KEYS.COLOR_SCHEME);
  if (savedColorScheme === 'light' || savedColorScheme === 'dark') {
    return savedColorScheme;
  }

  return typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

export interface AppProviderProps extends PropsWithChildren {
  /**
   * The telemetry logger, created once at boot in `src/main.tsx` and injected
   * here (TMP-style). Null / omitted disables telemetry — every downstream
   * call becomes a no-op.
   */
  biLogger?: TelemetryLogger | null;
}

export function AppProvider({ children, biLogger = null }: AppProviderProps) {
  const [colorScheme, setColorScheme] = useState(getInitialColorScheme);
  const scenarioFactory = useMemo(
    () => createScenarioFactory(biLogger ?? undefined),
    [biLogger],
  );
  const toggleColorScheme = useCallback(() => {
    setColorScheme((currentColorScheme) =>
      currentColorScheme === 'light' ? 'dark' : 'light',
    );
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.COLOR_SCHEME, colorScheme);
    } catch (error) {
      console.warn('Failed to persist color scheme to localStorage', error);
    }

    document.documentElement.style.colorScheme = colorScheme;
  }, [colorScheme]);

  // Emit a single boot scenario proving the operational pipeline end-to-end.
  // The ref guard makes this fire once even under StrictMode's double-mount.
  const bootFired = useRef(false);
  useEffect(() => {
    if (bootFired.current || !biLogger) return;
    bootFired.current = true;
    scenarioFactory.newScenario(ScenarioName.AppInit).complete('success');
  }, [biLogger, scenarioFactory]);

  const value = useMemo<AppState>(
    () => ({
      datasets: INITIAL_DATASETS,
      biLogger,
      scenarioFactory,
      colorScheme,
      toggleColorScheme,
    }),
    [biLogger, colorScheme, scenarioFactory, toggleColorScheme],
  );

  return (
    <FluentProvider
      theme={colorScheme === 'dark' ? teamsDarkTheme : teamsLightTheme}
      style={{ height: '100%' }}
    >
      <AppContext.Provider value={value}>{children}</AppContext.Provider>
    </FluentProvider>
  );
}

export function useAppContext(): AppState {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within <AppProvider>');
  }
  return context;
}

export function useOptionalAppContext(): AppState | null {
  return useContext(AppContext);
}
